/**
 * capabilities.js — the effective-capability manifest the UI gates on.
 *
 * Loaded once at bootstrap from GET /api-zapd/v1/me/capabilities, which returns
 * { role, caps:[...], modules:{name:bool} } (see zapd CapabilityResolver). `caps`
 * is the effective set (role default + per-user grants − global module toggles);
 * `modules` lets the UI tell "globally off" apart from "you lack the right".
 *
 * GRACEFUL BY DESIGN: the fetch is silent (no toast, no login-redirect, never
 * throws). Until it succeeds, `loaded()` stays false and callers fall back to the
 * legacy flags (flags.js) — so a missing endpoint (e.g. the engine not yet
 * redeployed) or a not-authed bootstrap leaves the SPA exactly as before.
 */

import { auth } from './auth.js';

let _loaded = false;
let _role = null;
const _caps = new Set();
const _modules = new Map();

/** Fetch + populate the manifest. Returns true on success, false on any failure
 *  (kept silent so it can run best-effort during bootstrap). */
export async function loadCapabilities() {
  try {
    const token = auth.getToken();
    const headers = token ? { Authorization: 'Bearer ' + token } : {};
    const r = await fetch('/api-zapd/v1/me/capabilities', {
      credentials: 'include',
      headers,
    });
    if (!r.ok) return false; // 404 (engine not redeployed) / 401 → silent fallback
    const json = await r.json();
    const data = json?.data ?? json; // ApiResponse {ok,data} or a raw object
    if (!data || !Array.isArray(data.caps)) return false;
    _caps.clear();
    _modules.clear();
    _role = data.role ?? null;
    for (const c of data.caps) _caps.add(c);
    for (const [name, on] of Object.entries(data.modules ?? {})) {
      _modules.set(name, on === true);
    }
    _loaded = true;
    return true;
  } catch {
    return false; // graceful: keep the legacy behaviour, never disrupt bootstrap
  }
}

/** The capability manifest accessor. While `loaded()` is false, `has`/`moduleOn`
 *  report nothing — callers must fall back to the legacy flags in that case. */
export const caps = {
  loaded: () => _loaded,
  role: () => _role,
  has: (cap) => _caps.has(cap),
  moduleOn: (name) => _modules.get(name) === true,
};

/** Effective gate for a capability with a legacy-flag fallback: once the manifest
 *  is loaded, returns caps.has(cap); until then (or if the fetch failed) returns
 *  `fallback` (the flags.js value), so the UI behaves exactly as before the
 *  manifest existed. The single helper the flag call-sites migrate onto in B3. */
export function gate(cap, fallback) {
  return _loaded ? _caps.has(cap) : fallback;
}
